<template>
    <v-form @submit.prevent="submitRateAlert">
        <v-card
            flat
            :loading="(loading.length>0 || innerLoading) && 'primary'"
        >
            <v-card-subtitle class="pa-6 body-2 text-center">
                I would like to receive an alert when offers...
            </v-card-subtitle>
            <v-card-text class="px-6 pb-0 pt-0 body-2">
                <!-- Select Poster Currency -->
                <v-row
                    class="align-baseline"
                    no-gutters
                >
                    <v-col class="flex-grow-0 flex-shrink-1 mr-4">
                        <v-avatar
                            color="primary lighten-3"
                        >
                            <v-img
                                v-if="alert.poster_currency"
                                :alt="alert.poster_currency"
                                :src="`/images/countries/${alert.poster_currency.toLowerCase()}.svg`"
                            />
                            <v-icon v-else>
                                mdi-flag-variant-outline
                            </v-icon>
                        </v-avatar>
                    </v-col>

                    <v-col>
                        <v-autocomplete
                            v-model="fromCountry"
                            label="From"
                            :items="filteredFromCountries"
                            item-text="country"
                            item-value="abbreviation"
                            item-color="primary"
                            return-object
                            color="primary"
                            outlined
                        />
                    </v-col>
                </v-row>

                <!-- Select Matcher Currency -->
                <v-row
                    class="align-baseline"
                    no-gutters
                >
                    <v-col class="flex-grow-0 flex-shrink-1 mr-4">
                        <v-avatar
                            color="primary lighten-3"
                        >
                            <v-img
                                v-if="alert.matcher_currency"
                                :alt="alert.matcher_currency"
                                :src="`/images/countries/${alert.matcher_currency.toLowerCase()}.svg`"
                            />
                            <v-icon v-else>
                                mdi-flag-variant-outline
                            </v-icon>
                        </v-avatar>
                    </v-col>

                    <v-col>
                        <v-autocomplete
                            v-model="toCountry"
                            label="To"
                            :items="filteredToCountries"
                            item-text="country"
                            item-value="abbreviation"
                            item-color="primary"
                            return-object
                            color="primary"
                            outlined
                        />
                    </v-col>
                </v-row>

                <!-- Rate Amount -->
                <v-text-field
                    v-model.number="alert.exchange_rate"
                    v-validate="`required|decimal`"
                    lazy-validation
                    label="Exceed the rate of..."
                    name="exchange-rate"
                    aria-required
                    required
                    :hint="`Suggested rates is between ${lowerBoundsOfSuggestedRange} and ${upperBoundsOfSuggestedRange}`"
                    :error-messages="errors.first('exchange-rate')"
                    color="primary"
                    outlined
                />
                <v-alert
                    v-if="rateAlertEnabled"
                    type="warning"
                    outlined
                    text
                >
                    Please contact support to have your email re-added to the rate alert mailing list before setting a rate alert if you previously unsubscribed from receiving rate alert by using the unsubscribe button in the rate alert emails
                </v-alert>
            </v-card-text>
            <v-card-actions class="pa-6">
                <v-switch
                    v-model="alert.active"
                    :label="`Alert for ${alert.poster_currency} / ${alert.matcher_currency} enabled.`"
                    color="primary"
                    :true-value="1"
                    :false-value="0"
                />
                <v-spacer />
                <v-btn
                    large
                    depressed
                    color="red darken-2"
                    class="white--text"
                    :disabled="!rateAlertEnabled"
                    @mousedown.native="deleteRateAlert"
                >
                    Remove
                </v-btn>

                <!-- Save -->
                <v-btn
                    large
                    depressed
                    color="primary"
                    class="white--text"
                    type="submit"
                    :disabled="errors.any()"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script type="text/x-template" id="alert-form-template">
    /* eslint-disable func-names */
    import formatCurrency from '../modules/formatCurrency';
    import ExchangeRateService from '../services/ExchangeRateService';

    const currency = require('./../modules/currency');

    export default {
        name: 'AlertForm',
        filters: {
            currency(amount) {
                return formatCurrency(amount);
            },
        },
        props: {
            alert: {
                type: Object,
                default() {
                    return {
                        poster_country: this.$store.state.preferences.filters.fromCountry.abbreviation,
                        poster_currency: this.$store.state.preferences.filters.fromCountry.currency,
                        matcher_country: this.$store.state.preferences.filters.toCountry.abbreviation,
                        matcher_currency: this.$store.state.preferences.filters.toCountry.currency,
                        exchange_rate: 0,
                        direction: 'above',
                        user_id: this.$store.state.user.id,
                        id: null,
                        created_at: '',
                        updated_at: '',
                    };
                },
            },
            loading: {
                type: Array,
                default() {
                    return [];
                },
            },
        },
        data: () => ({
            countries: [],
            innerLoading: false,
            exchangeRateLimits: {
                min: 0,
                max: 0,
            },
        }),
        computed: getComputed(),
        watch: getWatched(),
        created,
        mounted,
        methods: getMethods(),
    };

    function getComputed() {
        return {
            posterCurrency,
            matcherCurrency,
            filteredFromCountries,
            filteredToCountries,
            directionForWatcher,
            upperBoundsOfSuggestedRange,
            lowerBoundsOfSuggestedRange,
            rateAlertEnabled,
            isMatcherCurrencyStronger,
            strongerCurrency,
            weakerCurrency,
            fromCountry: {
                get() {
                    return this.alert.poster_country;
                },
                set(value) {
                    this.alert.poster_country = value.abbreviation;
                    this.alert.poster_currency = value.currency;
                },
            },
            toCountry: {
                get() {
                    return this.alert.matcher_country;
                },
                set(value) {
                    this.alert.matcher_country = value.abbreviation;
                    this.alert.matcher_currency = value.currency;
                },
            },
        };

        function posterCurrency() {
            const vm = this;
            const workingCurrency = currency.findCurrency(vm.alert.poster_currency);
            return currency.addFlagLink(workingCurrency);
        }

        function matcherCurrency() {
            const vm = this;
            const workingCurrency = currency.findCurrency(vm.alert.matcher_currency);
            return currency.addFlagLink(workingCurrency);
        }

        function isMatcherCurrencyStronger() {
            const matcherCurrencyCode = this.matcherCurrency.currency_code;
            const posterCurrencyCode = this.posterCurrency.currency_code;
            return currency.isAStronger(matcherCurrencyCode, posterCurrencyCode);
        }

        function strongerCurrency() {
            return this.isMatcherCurrencyStronger ? this.matcherCurrency : this.posterCurrency;
        }

        function weakerCurrency() {
            return this.isMatcherCurrencyStronger ? this.posterCurrency : this.matcherCurrency;
        }

        function filteredFromCountries() {
            return this.countries.filter((country) => country.sending);
        }

        function filteredToCountries() {
            return this.countries.filter((country) => country.receiving);
        }

        function directionForWatcher() {
            const vm = this;
            return vm.alert.direction;
        }

        function upperBoundsOfSuggestedRange() {
            return this.exchangeRateLimits.max.toFixed(2);
        }

        function lowerBoundsOfSuggestedRange() {
            return this.exchangeRateLimits.min.toFixed(2);
        }

        function rateAlertEnabled() {
            const vm = this;
            if (!vm.alert.id) {
                return false;
            }
            return true;
        }
    }

    function getWatched() {
        return {
            filteredToCurrencies,
            defaultRateId,
            'alert.poster_country': 'refreshExchangeRate',
            'alert.matcher_country': 'refreshExchangeRate',
        };

        function filteredToCurrencies() {
            const vm = this;
            if (!vm.filteredToCurrencies) {
                return;
            }

            const toCurrencyIsValid = vm.filteredToCurrencies.some((element) => element.currency_code === vm.alert.matcher_currency);
            if (!toCurrencyIsValid && !!vm.alert.matcher_currency) {
                vm.alert.matcher_currency = vm.filteredToCurrencies[0].currency_code;
            }
        }

        function defaultRateId() {
            const vm = this;
            if (vm.defaultRateId) {
                vm.$router.push({ name: 'rate-alert-create', params: { id: vm.defaultRateId } });
                vm.$store.commit('defaultRateAlertId', vm.defaultRateId);
            } else {
                vm.$router.push({ name: 'rate-alert-create' });
            }
        }
    }

    function created() {
        // const vm = this;
        // vm.$validator.pause();
    }

    function mounted() {
        this.refreshExchangeRate();
        this.loadCountries();
    }

    function getMethods() {
        return {
            deleteRateAlert,
            submitRateAlert,
            updateTabs,
            loadCountries,
            refreshExchangeRate,
        };

        function submitRateAlert() {
            const vm = this;
            vm.$emit('loading', 'rate alert');
            vm.$validator.validateAll().then(submitIfValid);

            function submitIfValid() {
                let payload = vm.alert;
                delete payload.created_at;
                delete payload.updated_at;
                payload = JSON.parse(JSON.stringify(payload));
                payload.user_id = vm.$store.state.user.id;
                if (payload.id) {
                    Vue.backendApi().rateAlert().updateRateAlert(payload).then(success, error);
                } else {
                    Vue.backendApi().rateAlert().storeRateAlert(payload).then(success, error);
                }

                function success(response) {
                    vm.defaultRateId = response.data.id;
                    vm.$swal({
                        type: 'success',
                        title: 'Rate alert saved',
                        text: `You'll now receive an alert when offers are created with rates above ${vm.alert.exchange_rate}  ${vm.alert.matcher_currency} per 1 ${vm.alert.poster_currency}.`,
                    });
                    vm.updateTabs();
                }

                function error(err) {
                    vm.$swal({
                        type: 'error',
                        title: 'Error creating rate alert',
                        text: err,
                    });
                    vm.updateTabs();
                }
            }
        }

        function deleteRateAlert() {
            const vm = this;
            vm.$emit('loading', 'rate alert');
            const payload = vm.alert.id;
            Vue.backendApi().rateAlert().deleteRateAlert(payload).then(success, error);

            function success(response) {
                vm.updateTabs();
            }

            function error(err) {
                vm.$swal({
                    type: 'error',
                    title: 'Error disabling rate alert',
                    text: err,
                });
                vm.alert = {};
                vm.updateTabs();
            }
        }

        function updateTabs() {
            const vm = this;
            vm.$emit('updateTabs');
        }

        async function loadCountries() {
            try {
                this.innerLoading = true;
                const { data } = await window.Vue.backendApi()
                    .countries()
                    .list();
                this.countries = data;
            } catch (err) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading countries',
                    text: err,
                });
            } finally {
                this.innerLoading = false;
            }
        }

        async function refreshExchangeRate() {
            const matcherCurrency = this.alert.matcher_currency;
            const posterCurrency = this.alert.poster_currency;
            const posterCountry = this.alert.poster_country;

            this.innerLoading = true;
            const limits = await ExchangeRateService.getLimits(posterCurrency, matcherCurrency, posterCountry);
            this.exchangeRateLimits = limits;
            const suggestedExchangeRate = (limits.max + limits.min)/2;
            this.alert.exchange_rate = Math.round(suggestedExchangeRate * 100) / 100;
            this.innerLoading = false;
        }
    }
</script>
